import { Alert, Box, Button, CircularProgress, DialogActions, Grid } from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import EnrollmentDetailsForm from "./EnrollmentDetailsForm";
import EntitySelectionPanel from "./EntitySelectionPanel";
import ReviewTable from "./ReviewTable";
import useBulkEnrollment from "./useBulkEnrollment";

const BulkEnrollmentPage = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    pageTitle,
    isRootOrganization,
    detailsDisabled,
    isUserLocked,
    isSkuLocked,
    lockedUserLoading,
    lockedUserLoadError,
    backUrl,
    backLabelKey,
    hasUnresolvedStatus,
    hasLockedActiveRegistration,
    endDate,
    setEndDate,
    crmOrderName,
    crmOrderNameError,
    setCrmOrderName,
    isTest,
    setIsTest,
    bypassSkuExpiration,
    setBypassSkuExpiration,
    effectiveUsers,
    setSelectedUsers,
    effectiveSkus,
    setSelectedSkus,
    userQuery,
    skuQuery,
    setUserQuery,
    setSkuQuery,
    userOptions,
    skuOptions,
    addUser,
    addSku,
    handleUserPaste,
    handleSkuPaste,
    rows,
    statusByPair,
    statusFetching,
    newCount,
    extendCount,
    submit,
    submitting,
    showDialog,
    showDialogActions,
    snackbar,
    setSnackbar,
  } = useBulkEnrollment();

  if (lockedUserLoading) {
    return <Loader />;
  }

  return (
    <>
      <RedirectBackButton redirectRoute={backUrl} labelKey={backLabelKey} />

      <h1>{pageTitle}</h1>

      {lockedUserLoadError && (
        <Box mt={2} mb={3}>
          <Alert severity="error">
            <T id="userLoadError" />
          </Alert>
        </Box>
      )}

      {isRootOrganization && (
        <Box mt={2} mb={3}>
          <Alert severity="warning">
            <T id="rootOrganizationIsNotAllowedTooltip" />
          </Alert>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EnrollmentDetailsForm
            disabled={detailsDisabled}
            endDate={endDate}
            onEndDateChange={setEndDate}
            crmOrderName={crmOrderName}
            crmOrderNameError={crmOrderNameError}
            onCrmOrderNameChange={setCrmOrderName}
            isTest={isTest}
            onIsTestChange={setIsTest}
            bypassSkuExpiration={bypassSkuExpiration}
            onBypassSkuExpirationChange={setBypassSkuExpiration}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <EntitySelectionPanel
            title={t({ id: "users", defaultMessage: "Users" })}
            disabled={detailsDisabled}
            isLocked={isUserLocked}
            options={userOptions}
            value={effectiveUsers}
            inputValue={userQuery}
            placeholder={t({
              id: "bulkRegistrationUsersPlaceholder",
              defaultMessage: "Search by email or paste multiple emails...",
            })}
            noOptionsText={t({
              id: "bulkRegistrationTypeToSearch",
              defaultMessage: "Type at least 2 characters to search",
            })}
            getId={(user) => user.userId}
            getPrimary={(user) => user.email}
            getSecondary={(user) => user.organizationName}
            onInputChange={setUserQuery}
            onAdd={addUser}
            onSetValue={setSelectedUsers}
            onPaste={handleUserPaste}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <EntitySelectionPanel
            title={t({ id: "skus", defaultMessage: "SKUs" })}
            disabled={detailsDisabled}
            isLocked={isSkuLocked}
            options={skuOptions}
            value={effectiveSkus}
            inputValue={skuQuery}
            placeholder={t({
              id: "bulkRegistrationSkusPlaceholder",
              defaultMessage: "Search by SKU code or title, or paste multiple codes...",
            })}
            noOptionsText={t({
              id: "bulkRegistrationTypeToSearch",
              defaultMessage: "Type at least 2 characters to search",
            })}
            getId={(sku) => sku.skuId}
            getPrimary={(sku) => sku.code}
            getSecondary={(sku) => sku.title}
            onInputChange={setSkuQuery}
            onAdd={addSku}
            onSetValue={setSelectedSkus}
            onPaste={handleSkuPaste}
          />
        </Grid>

        <Grid item xs={12}>
          <ReviewTable
            rows={rows}
            statusByPair={statusByPair}
            isFetching={statusFetching}
            newCount={newCount}
            extendCount={extendCount}
          />
        </Grid>
      </Grid>

      {hasLockedActiveRegistration && (
        <Box mt={2}>
          <Alert severity="warning">{t({ id: "userAlreadyHasActiveRegistration" })}</Alert>
        </Box>
      )}

      <DialogActions sx={{ px: 0, pt: 3 }}>
        <Button disabled={submitting} onClick={() => navigate(backUrl)}>
          {t({ id: "cancelButton" })}
        </Button>
        <Button
          type="button"
          color="primary"
          variant="contained"
          disabled={
            submitting ||
            rows.length === 0 ||
            isRootOrganization ||
            hasUnresolvedStatus ||
            hasLockedActiveRegistration
          }
          onClick={submit}
        >
          {submitting ? (
            <CircularProgress size={24} />
          ) : (
            t({ id: "bulkRegistrationRegister", defaultMessage: "Register" })
          )}
        </Button>
      </DialogActions>

      <ConfirmDialog
        dialogTitleKey="bulkEnrollmentCompleted"
        contentKey="bulkRegistrationBackgroundNotice"
        open={showDialog}
        onCancel={showDialogActions.setFalse}
        handleConfirm={() => navigate(backUrl)}
        confirmLabel={backLabelKey}
        cancelLabel="closeButton"
      />

      <SnackbarMessage snackbarMessage={snackbar} setSnackbarMessage={setSnackbar} />
    </>
  );
};

export default BulkEnrollmentPage;
